import 'prop-types';
import { formatNumber, formatCurrency } from '@atc/string-fns';
import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import _get from 'lodash/get';
import { getAppliedDealerSettings, calculateDownPayment, getPersonalizedPaymentParams, getPersonalizedPaymentDetails, getLowestPersonalizedPayment } from '@atc/wallet-payment-fns';
import { Text, ModalTrigger, Modal, ModalHeader } from 'reaxl';
import { useFeatures } from '@atc/features';
import { defaultPaymentInfo } from 'axl-config';
import getLeaseOffer from '../utilities/getLeaseOffer.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
const Payments = /*#__PURE__*/React.memo(_ref => {
  let {
    className = '',
    onClick = () => {},
    payments,
    personalPaymentProps = {},
    showPaymentDetailsLink,
    orientation,
    listingId,
    ...rest
  } = _ref;
  const nationalRates = useSelector(state => state.interestRates) || {};
  const inventory = useSelector(state => state.inventory) || {};
  const listing = inventory[listingId] || {};
  const {
    srp_default_ps_payments: [enableDefaultPS]
  } = useFeatures(['srp_default_ps_payments']);
  const [isLoading, setIsLoading] = useState(false);
  const [defaultPayment, setDefaultPayment] = useState(null);
  const {
    fetcher: fetchPayments,
    paymentsEnabled = true
  } = personalPaymentProps;
  const {
    paymentServices = {},
    zip,
    pricingDetail,
    type = 'used'
  } = listing;

  // TODO: Replace with dealer setting once available US810522
  const {
    loanTerm,
    leaseTerm,
    leaseMileageRange,
    interactions
  } = defaultPaymentInfo;
  const {
    paymentCalculationUrl,
    dealerSettings = {},
    digitalRetailingType
  } = paymentServices;
  const {
    defaultDealType = 'FINANCE'
  } = dealerSettings;
  useEffect(() => {
    const fetchPersonalizedPayment = async () => {
      if (!!fetchPayments && paymentCalculationUrl && listingId) {
        if (digitalRetailingType === 'accelerate') {
          setIsLoading(true);
          const {
            downPayment: downPaymentValue,
            downPaymentPercent: downPaymentPercentValue,
            creditType: creditTypeValue
          } = getAppliedDealerSettings({
            dealerSettings,
            walletInteractions: interactions,
            payments: defaultPaymentInfo,
            nationalRates
          });
          const {
            msrp,
            salePrice
          } = pricingDetail;
          const price = salePrice || msrp;

          // TODO: Replace with dealer setting once available US810522
          const leaseDownPaymentFallBack = calculateDownPayment(price, {
            downPayment: Math.floor(price * 0.1) || null,
            downPaymentPercent: 0.1
          });
          let leaseParams = {};
          if (defaultDealType === 'LEASE') {
            leaseParams = {
              leaseTerm,
              milesPerYear: leaseMileageRange,
              leaseDownPayment: leaseDownPaymentFallBack
            };
          }
          const _paymentsInfo = {
            loanTerm,
            creditType: creditTypeValue,
            downPayment: downPaymentValue,
            downPaymentPercent: downPaymentPercentValue,
            tradeIn: {},
            ...leaseParams
          };
          const personalizedPaymentParams = getPersonalizedPaymentParams({
            paymentsInfo: _paymentsInfo,
            userZip: zip
          });
          const {
            details,
            error
          } = await getPersonalizedPaymentDetails({
            fetchPayments,
            paymentCalculationUrl,
            personalizedPaymentParams
          });
          if (error || details?.error) {
            setDefaultPayment(null);
          }
          if (details && Object.keys(details.rates).length) {
            if (defaultDealType === 'LEASE') {
              const leasePayment = await getLeaseOffer(details, creditTypeValue, leaseTerm);
              setDefaultPayment(leasePayment);
            } else {
              const payment = await getLowestPersonalizedPayment(details, creditTypeValue, loanTerm);
              setDefaultPayment(payment);
            }
          }
        }
      }
      setIsLoading(false);
    };
    if (enableDefaultPS && paymentsEnabled) {
      fetchPersonalizedPayment();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId]);
  let disclaimer;
  let paymentLabel;
  let paymentValue;
  if (enableDefaultPS) {
    const vehicleCondition = type.toLowerCase() === 'certified' ? 'used' : type.toLowerCase();
    const paymentServiceRate = _get(defaultPayment, vehicleCondition);
    // TODO: replace once disclaimer redesign decided on for default payments
    if (defaultDealType === 'LEASE') {
      disclaimer = `Estimated payment based on ${formatNumber({
        value: defaultPayment?.annualMiles
      })} miles/year for ${leaseTerm} months with $${formatNumber({
        value: defaultPayment?.downPayment
      })} down for well qualified buyers. Terms may vary.`;
      paymentLabel = 'Est. Lease Payment';
    } else {
      disclaimer = `Estimated payment based on ${paymentServiceRate}% APR for ${loanTerm} months with $${formatNumber({
        value: defaultPayment?.downPayment
      })} down for well qualified buyers. Terms may vary.`;
      paymentLabel = 'Est. Finance Payment';
    }
    paymentValue = defaultPayment?.payment;
  } else {
    disclaimer = payments?.paymentDisclaimer;
    paymentLabel = payments?.label;
    paymentValue = payments?.value;
  }
  const renderDisclaimerModal = () => /*#__PURE__*/jsxs(Modal, {
    bsSize: "small",
    show: true,
    onHide: () => {},
    children: [/*#__PURE__*/jsx(ModalHeader, {
      closeButton: true,
      children: /*#__PURE__*/jsx(Modal.Title, {
        children: "Payment Details"
      })
    }), /*#__PURE__*/jsx(Modal.Body, {
      children: disclaimer
    })]
  }, "renderDisclaimerModal");
  const renderViewPayments = () => /*#__PURE__*/jsx(ModalTrigger, {
    modal: renderDisclaimerModal(),
    onClick: onClick,
    children: /*#__PURE__*/jsx(Text, {
      size: 100,
      className: "text-link",
      componentClass: "div",
      children: "See payment details"
    }, "renderViewPaymentsModalTrigger")
  });
  const renderDefaultPayments = () => /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsxs(Text, {
      color: "subdued-lighter",
      children: [paymentLabel, " "]
    }), /*#__PURE__*/jsxs(Text, {
      color: "subdued-lighter",
      size: 100,
      weight: "bold",
      className: "display-block display-sm-inline",
      children: [formatCurrency(Math.floor(paymentValue)), "/mo."]
    }), renderViewPayments()]
  });
  const renderPayments = !enableDefaultPS && !!payments || enableDefaultPS && !isLoading && !!defaultPayment;
  return renderPayments && /*#__PURE__*/jsx("div", {
    "data-cmp": "payments",
    className: clsx('text-size-100', className),
    ...rest,
    children: renderDefaultPayments()
  });
});
export { Payments as default };
